import type { UnknownAction } from '@reduxjs/toolkit';
import { isEmpty, isNotDefined } from '@sgme/fp';
import { unminifyError } from '@sgme/stacktrace';
import { Component, type ErrorInfo, type PropsWithChildren } from 'react';
import { connect, type DispatchProp } from 'react-redux';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { SCREENSHOT_QUALITY } from '@/components/fatality/const.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { take as takeScreenshot } from '@/utils/screenshot.ts';

type Props = PropsWithChildren<
  {
    tileId: string;
  } & DispatchProp
>;

type State = {
  error: undefined;
};

class UnconnectedTileCatchAll extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error) {
    return { error: error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('===== Tile error boundary caught :');
    console.error('=== error', error);
    console.error('=== errorInfo', errorInfo);
    logger.logError('Error boundary {error_s} {errorInfo_s}', JSON.stringify(error), JSON.stringify(errorInfo));

    const buildDetails = unminifyError(error).then((unminifiedError) => {
      let details = `APP_CRASHED can't unminify stack for error: ${JSON.stringify(error)}`;

      const errorMessage = unminifiedError?.message;
      const beautifulStackTrace = unminifiedError?.stack;

      if (typeof beautifulStackTrace === 'string' && !isEmpty(beautifulStackTrace)) {
        if (typeof errorMessage === 'string' && !isEmpty(errorMessage)) {
          details = `Automatic help request sent after tile crashed (quoteId: ${this.props.tileId})\n\n${errorMessage}:\n${beautifulStackTrace}`;
        }
      }
      return details;
    });

    const buildScreenshot = takeScreenshot({
      element: document.body,
      shouldIgnoreElement: () => false,
    }).then((canvas) => canvas.toDataURL('image/jpeg', SCREENSHOT_QUALITY));

    Promise.all([buildDetails, buildScreenshot]).then(([details, screenshot]) => {
      this.props.dispatch(
        sendHelpRequestThunk({
          config: 'appcrashed', // maybe would be 'tilecrashed' if exist
          details,
          screenshot,
          templateId: getConfig().app.mailTemplateIds.templateIdForAppCrashed,
        }) as unknown as UnknownAction,
      );
    });
  }

  render() {
    if (isNotDefined(this.state.error)) {
      return this.props.children;
    }

    return <ErrorTile tileId={this.props.tileId} errorDescription="fatal.tilecrash.defaultDescription" />;
  }
}

export const TileCatchAll = connect()(UnconnectedTileCatchAll);
